import { useCallback, useEffect, useMemo, useState } from "react";
import { Select } from "../../select/Select";
import { TextInput } from "../../text-input/TextInput";
import styles from "./index.module.scss";
import { Button } from "components/buttons/Button";
import CloseIcon from "assets/icons/close-blue.svg?react";
import { shorten } from "helpers/strings";
import classnames from "classnames/bind";
import { useDebounce } from "hooks/useDebounce";
import { LoadingOverlay } from "components/loading-overlay";
const cx = classnames.bind(styles);

type StringKeys<T> = {
    [K in keyof T]: T[K] extends string ? K : never;
}[keyof T];

interface BaseAssignmentProps<T> {
    items: T[];
    itemIdKey: StringKeys<T>;
    itemLabelKey: StringKeys<T>;
    itemImageKey?: StringKeys<T>;
    assignedItems: T[];
    handleAssignItem: (item: T) => void;
    handleAddItem: (item: string) => void;
    handleRemoveItem: (item: string) => void;
    itemsLoading?: boolean;
    labels?: {
        appliedItemsLabel?: string;
        noItemsAppliedLabel?: string;
        selectLabel?: string;
        selectPlaceholder?: string;
        inputLabel?: string;
        inputPlaceholder?: string;
        addLabel?: string;
    };
    variant: "add" | "search";
    isMultiple?: boolean; // Whether the assignment allows multiple selections
}

export const BaseAssignment = <T,>({
    items,
    assignedItems,
    itemIdKey,
    itemLabelKey,
    itemImageKey,
    handleAssignItem,
    handleAddItem,
    handleRemoveItem,
    itemsLoading = false,
    labels: {
        appliedItemsLabel = "Applied Items",
        noItemsAppliedLabel = "No Items Applied",
        selectLabel = "Select Item",
        selectPlaceholder = "Choose an item",
        inputLabel = "Add Item",
        inputPlaceholder = "Type to add",
        addLabel = "Add"
    } = {},
    variant,
    isMultiple = true
}: BaseAssignmentProps<T>) => {
    const [assignmentInput, setAssignmentInput] = useState<string>("");

    // In the search variant, debounce the value and trigger the add action
    const { value: debouncedAssignment, cancel: cancelDebounce } =
        useDebounce<string>(assignmentInput, 1000);
    useEffect(() => {
        if (variant !== "search") return;
        if (debouncedAssignment) {
            handleAddItem(debouncedAssignment);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant, debouncedAssignment]);

    // Handle Enter key to trigger add and cancel debounce
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (variant !== "search") return;
        if (e.key === "Enter" && assignmentInput) {
            cancelDebounce();
            handleAddItem(assignmentInput);
        }
    };

    const handleAddItemClick = useCallback(() => {
        handleAddItem(assignmentInput);
        setAssignmentInput("");
    }, [handleAddItem, assignmentInput]);

    const handleAssignedItemChange = useCallback(
        (itemId: string) => {
            const assignedItem = items.find(
                (item) => item[itemIdKey] === itemId
            );
            if (assignedItem) {
                handleAssignItem(assignedItem);
            }
        },
        [items, itemIdKey, handleAssignItem]
    );

    const handleRemoveItemClick = useCallback(
        (itemId: string) => {
            handleRemoveItem(itemId);
        },
        [handleRemoveItem]
    );

    const nonAssignedItems = useMemo(() => {
        return items.filter((item) => {
            return !assignedItems.some(
                (assignedItem) => assignedItem[itemIdKey] === item[itemIdKey]
            );
        });
    }, [items, assignedItems, itemIdKey]);

    return (
        <div className={cx("base-assignment")}>
            <div className={cx("assigned-items-container")}>
                <h6>{appliedItemsLabel}</h6>
                {assignedItems.length === 0 ? (
                    <div className={cx("no-items-assigned")}>
                        <p>{noItemsAppliedLabel}</p>
                    </div>
                ) : (
                    <div className={cx("assigned-items")}>
                        {assignedItems.map((item) => {
                            return (
                                <Button
                                    onClick={(c) =>
                                        handleRemoveItemClick(
                                            c.currentTarget.value
                                        )
                                    }
                                    type="assignment"
                                    value={item[itemIdKey] as string}
                                    iconRight={<CloseIcon />}
                                    key={item[itemIdKey] as string}
                                >
                                    {itemImageKey && item[itemImageKey] && (
                                        <img
                                            src={item[itemImageKey] as string}
                                            alt={item[itemLabelKey] as string}
                                        />
                                    )}
                                    <div className={cx("assignment-label")}>
                                        {shorten(
                                            item[itemLabelKey] as string,
                                            15
                                        )}
                                    </div>
                                </Button>
                            );
                        })}
                    </div>
                )}
            </div>
            <div
                className={cx("assignment-container", {
                    "is-search": variant === "search"
                })}
            >
                <LoadingOverlay
                    isLoading={itemsLoading}
                    targetInteractiveCssSelector=".form-group"
                    transparentOnFirstLoad={false}
                    transparentOnLoad={true}
                    inlineLoading={true}
                >
                    <Select
                        id="assignment-dropdown"
                        onChange={(c) =>
                            handleAssignedItemChange(c.target.value)
                        }
                        options={nonAssignedItems.map((item) => ({
                            value: item[itemIdKey] as string,
                            text: item[itemLabelKey] as string
                        }))}
                        placeholder={selectPlaceholder}
                        type="assignment-dropdown"
                        label={selectLabel}
                        disabled={
                            !nonAssignedItems?.length ||
                            (!isMultiple && assignedItems.length > 0)
                        }
                    />
                </LoadingOverlay>

                <TextInput
                    id="assignment-input"
                    type="text"
                    value={assignmentInput}
                    className={"assignment-input"}
                    onChange={(c) =>
                        setAssignmentInput(c.target.value?.trim() ?? "")
                    }
                    onKeyDown={handleInputKeyDown}
                    label={inputLabel}
                    placeholder={inputPlaceholder ?? ""}
                />

                {variant === "add" && (
                    <span
                        className={cx("add-item-button")}
                        onClick={handleAddItemClick}
                        aria-disabled={assignmentInput === ""}
                    >
                        {addLabel}
                    </span>
                )}
            </div>
        </div>
    );
};
